import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import Layout from '../components/layout/Layout';
import { useToast } from '../contexts/ToastContext';
import { Permission, rbacService } from '../services/rbacService';

// Column/action display order for the permission matrix. `import` and `export`
// are kept adjacent so the two sheet actions sit side by side.
const ACTION_ORDER = ['create', 'submit', 'approve', 'view', 'form-view', 'download', 'update', 'import', 'export', 'assign-dock', 'manage', 'assign-permissions'];

const actionRank = (action: string) => {
  const index = ACTION_ORDER.indexOf(action);
  return index === -1 ? 999 : index;
};

export default function RolesPermissionsPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [draftPermissionIds, setDraftPermissionIds] = useState<number[]>([]);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDescription, setNewRoleDescription] = useState('');
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const { data: permissions = [], isLoading: permissionsLoading } = useQuery({
    queryKey: ['rbac', 'permissions'],
    queryFn: rbacService.getPermissions,
  });

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ['rbac', 'roles'],
    queryFn: rbacService.getRoles,
  });

  useEffect(() => {
    if (!selectedRoleId && roles.length > 0) {
      setSelectedRoleId(roles[0].id);
    }
  }, [roles, selectedRoleId]);

  const selectedRole = useMemo(
    () => roles.find((role) => role.id === selectedRoleId) ?? null,
    [roles, selectedRoleId],
  );

  useEffect(() => {
    if (!selectedRole) {
      setDraftPermissionIds([]);
      setEditName('');
      setEditDescription('');
      return;
    }

    setDraftPermissionIds(
      selectedRole.rolePermissions.map((rolePermission) => rolePermission.permission.id),
    );
    setEditName(selectedRole.name);
    setEditDescription(selectedRole.description ?? '');
  }, [selectedRole]);

  const permissionMatrix = useMemo(() => {
    const grouped = permissions.reduce<Record<string, Permission[]>>((acc, permission) => {
      acc[permission.module] = acc[permission.module] ?? [];
      acc[permission.module].push(permission);
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([module, modulePermissions]) => {
        const permissionsByAction = modulePermissions.reduce<Record<string, Permission>>(
          (acc, permission) => {
            const action = permission.name.split(':')[1] ?? permission.name;
            acc[action] = permission;
            return acc;
          },
          {},
        );

        const actions = Object.keys(permissionsByAction).sort(
          (left, right) => actionRank(left) - actionRank(right) || left.localeCompare(right),
        );

        return {
          module,
          actions,
          permissionsByAction,
        };
      });
  }, [permissions]);

  const allActions = useMemo(
    () =>
      Array.from(new Set(permissionMatrix.flatMap((row) => row.actions))).sort(
        (left, right) => actionRank(left) - actionRank(right) || left.localeCompare(right),
      ),
    [permissionMatrix],
  );

  const createRoleMutation = useMutation({
    mutationFn: rbacService.createRole,
    onSuccess: (role) => {
      showToast('Role created successfully', 'success');
      setNewRoleName('');
      setNewRoleDescription('');
      queryClient.invalidateQueries({ queryKey: ['rbac', 'roles'] });
      setSelectedRoleId(role.id);
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to create role', 'error');
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: (payload: { roleId: number; name?: string; description?: string }) =>
      rbacService.updateRole(payload.roleId, {
        name: payload.name,
        description: payload.description,
      }),
    onSuccess: () => {
      showToast('Role updated successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['rbac', 'roles'] });
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to update role', 'error');
    },
  });

  const assignPermissionsMutation = useMutation({
    mutationFn: (payload: { roleId: number; permissionIds: number[] }) =>
      rbacService.assignPermissions(payload.roleId, payload.permissionIds),
    onSuccess: () => {
      showToast('Role permissions updated successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['rbac', 'roles'] });
    },
    onError: (error: any) => {
      showToast(error?.message || 'Failed to update permissions', 'error');
    },
  });

  const handleCreateRole = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!newRoleName.trim()) {
      return;
    }

    createRoleMutation.mutate({
      name: newRoleName.trim().toUpperCase(),
      description: newRoleDescription.trim() || undefined,
    });
  };

  const togglePermission = (permissionId: number) => {
    setDraftPermissionIds((current) =>
      current.includes(permissionId)
        ? current.filter((id) => id !== permissionId)
        : [...current, permissionId],
    );
  };

  const allPermissionIds = useMemo(
    () => permissions.map((permission) => permission.id),
    [permissions],
  );

  const allPermissionsSelected =
    allPermissionIds.length > 0 &&
    allPermissionIds.every((id) => draftPermissionIds.includes(id));

  const toggleSelectAllPermissions = () => {
    setDraftPermissionIds(allPermissionsSelected ? [] : allPermissionIds);
  };

  // Mobile accordion state - which module cards are expanded
  const [expandedModules, setExpandedModules] = useState<Record<string, boolean>>({});

  const toggleModuleExpanded = (module: string) => {
    setExpandedModules((current) => ({ ...current, [module]: !current[module] }));
  };

  // Grant or revoke every permission of one module at once
  const toggleModuleAll = (modulePermissionIds: number[], allGranted: boolean) => {
    setDraftPermissionIds((current) =>
      allGranted
        ? current.filter((id) => !modulePermissionIds.includes(id))
        : Array.from(new Set([...current, ...modulePermissionIds])),
    );
  };

  const handleSaveRoleDetails = () => {
    if (!selectedRole) {
      return;
    }

    updateRoleMutation.mutate({
      roleId: selectedRole.id,
      name: editName.trim().toUpperCase(),
      description: editDescription.trim() || '',
    });
  };

  const handleSavePermissions = () => {
    if (!selectedRole) {
      return;
    }

    assignPermissionsMutation.mutate({
      roleId: selectedRole.id,
      permissionIds: draftPermissionIds,
    });
  };

  return (
    <Layout>
      <div className="min-h-screen px-1 py-4 lg:p-4 bg-gradient-to-br from-gray-50 to-gray-100 scrollbar-hide">
        {/* Header - Matching Client Management Design */}
        <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
          <div className="flex items-center space-x-3 bg-gradient-to-r from-brand-100 to-brand-50 p-5 rounded-xl shadow-lg border-2 border-brand-300 hover:shadow-xl transition-shadow duration-300">
            <div className="p-3 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl shadow-md transform transition-transform duration-300 hover:scale-110 hover:rotate-3">
              <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Roles & Permissions</h2>
              <p className="text-gray-600 mt-1">
                Manage dynamic roles, assign permission-level access, and control enterprise workflows centrally.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[320px_minmax(0,1fr)] gap-4">
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl">
              <div className="p-5 bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-gray-900">Roles</h3>
                  </div>
                  {rolesLoading && (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-200 border-t-blue-600"></div>
                      <span className="text-xs text-gray-500">Loading...</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-5 space-y-2 max-h-[400px] overflow-y-auto">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    onClick={() => setSelectedRoleId(role.id)}
                    className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all duration-300 transform hover:scale-[1.02] ${
                      selectedRoleId === role.id
                        ? 'border-brand-500 bg-gradient-to-r from-brand-50 to-brand-100 shadow-md'
                        : 'border-gray-300 hover:border-brand-300 hover:bg-gray-50 hover:shadow-sm'
                    }`}
                  >
                    <div className="font-semibold text-gray-900">{role.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {role.description || 'No description'}
                    </div>
                    <div className="flex items-center mt-2">
                      <svg className="w-3 h-3 text-brand-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span className="text-xs text-gray-500">
                        {role.rolePermissions.length} permissions assigned
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <form
              onSubmit={handleCreateRole}
              className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl"
            >
              <div className="p-5 bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                <div className="flex items-center">
                  <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <h3 className="text-lg font-semibold text-gray-900">Create Role</h3>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
                  <input
                    value={newRoleName}
                    onChange={(event) => setNewRoleName(event.target.value)}
                    placeholder="e.g. AUDITOR"
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 hover:border-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={newRoleDescription}
                    onChange={(event) => setNewRoleDescription(event.target.value)}
                    rows={3}
                    placeholder="Explain what this role is allowed to do"
                    className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 hover:border-gray-400"
                  />
                </div>
                <button
                  type="submit"
                  disabled={createRoleMutation.isPending}
                  className="btn-primary w-full transform transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center space-x-2"
                >
                  {createRoleMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>Creating...</span>
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      <span>Create Role</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-300 overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:border-primary-300">
            {!selectedRole ? (
              <div className="h-full min-h-[420px] flex items-center justify-center p-12">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <p className="text-gray-600 font-semibold">Select a role to manage its permissions.</p>
                  <p className="text-gray-500 text-sm mt-2">Choose a role from the list on the left to get started.</p>
                </div>
              </div>
            ) : (
              <div>
                <div className="p-5 bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-4 items-end">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Role Name</label>
                      <input
                        value={editName}
                        onChange={(event) => setEditName(event.target.value)}
                        className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 hover:border-gray-400"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                      <input
                        value={editDescription}
                        onChange={(event) => setEditDescription(event.target.value)}
                        className="w-full px-3 py-2.5 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 hover:border-gray-400"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleSaveRoleDetails}
                      disabled={updateRoleMutation.isPending}
                      className="px-4 py-2.5 text-sm font-medium border rounded-xl flex items-center space-x-2 transition-all duration-200 shadow-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {updateRoleMutation.isPending ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-600 border-t-transparent"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Save Role</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="p-5 bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center">
                      <svg className="w-6 h-6 text-brand-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">Permission Matrix</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Toggle permission-level access for <span className="font-semibold">{selectedRole.name}</span>.
                        </p>
                        <p className="text-xs text-brand-700 font-medium mt-1 lg:hidden">
                          {draftPermissionIds.length} of {allPermissionIds.length} permissions enabled
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 w-full lg:w-auto">
                    <button
                      type="button"
                      onClick={toggleSelectAllPermissions}
                      disabled={permissionsLoading || allPermissionIds.length === 0}
                      className="flex-1 lg:flex-none justify-center px-4 py-2.5 min-h-[44px] lg:min-h-0 text-sm font-medium border rounded-xl flex items-center space-x-2 transition-all duration-200 shadow-sm bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={allPermissionsSelected ? 'M6 18L18 6M6 6l12 12' : 'M5 13l4 4L19 7'} />
                      </svg>
                      <span>{allPermissionsSelected ? 'Deselect All' : 'Select All'}</span>
                    </button>
                    <button
                      type="button"
                      onClick={handleSavePermissions}
                      disabled={assignPermissionsMutation.isPending || permissionsLoading}
                      className="btn-primary flex-1 lg:flex-none justify-center transform transition-all duration-300 hover:scale-105 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
                    >
                      {assignPermissionsMutation.isPending ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Save Permissions</span>
                        </>
                      )}
                    </button>
                    </div>
                  </div>
                </div>

                <div className="px-1 py-3 lg:p-5">
                  {/* Mobile: one accordion card per module with toggle switches */}
                  <div className="lg:hidden space-y-2.5 px-1">
                    {permissionMatrix.map((row) => {
                      const modulePermissions = row.actions
                        .map((action) => row.permissionsByAction[action])
                        .filter(Boolean);
                      const modulePermissionIds = modulePermissions.map((permission) => permission.id);
                      const grantedCount = modulePermissionIds.filter((id) =>
                        draftPermissionIds.includes(id),
                      ).length;
                      const allGranted = grantedCount === modulePermissionIds.length && grantedCount > 0;
                      const isExpanded = !!expandedModules[row.module];

                      return (
                        <div key={row.module} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                          {/* Module header - tap to expand */}
                          <button
                            type="button"
                            onClick={() => toggleModuleExpanded(row.module)}
                            className="w-full flex items-center justify-between gap-2 px-4 py-3 min-h-[52px] active:bg-brand-50 transition-colors"
                          >
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-sm font-bold text-gray-900 uppercase truncate">{row.module}</span>
                              <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${
                                grantedCount === 0
                                  ? 'bg-gray-100 text-gray-500'
                                  : allGranted
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-brand-100 text-brand-700'
                              }`}>
                                {grantedCount}/{modulePermissionIds.length}
                              </span>
                            </div>
                            <svg
                              className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>

                          {isExpanded && (
                            <div className="border-t border-gray-100">
                              {/* Allow-all switch for the whole module */}
                              <div className="flex items-center justify-between gap-3 px-4 py-2.5 min-h-[48px] bg-gray-50 border-b border-gray-100">
                                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Allow all</span>
                                <button
                                  type="button"
                                  role="switch"
                                  aria-checked={allGranted}
                                  onClick={() => toggleModuleAll(modulePermissionIds, allGranted)}
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 ${allGranted ? 'bg-brand-600' : 'bg-gray-300'}`}
                                >
                                  <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-200 ${allGranted ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                                </button>
                              </div>

                              {/* One switch row per permission */}
                              {row.actions.map((action) => {
                                const permission = row.permissionsByAction[action];
                                if (!permission) return null;
                                const isChecked = draftPermissionIds.includes(permission.id);
                                return (
                                  <div
                                    key={permission.id}
                                    className="flex items-center justify-between gap-3 px-4 py-2.5 min-h-[48px] border-b border-gray-50 last:border-b-0"
                                  >
                                    <div className="min-w-0">
                                      <p className="text-sm font-medium text-gray-800 capitalize">{action.replace('-', ' ')}</p>
                                      {permission.description && (
                                        <p className="text-xs text-gray-500 truncate">{permission.description}</p>
                                      )}
                                    </div>
                                    <button
                                      type="button"
                                      role="switch"
                                      aria-checked={isChecked}
                                      onClick={() => togglePermission(permission.id)}
                                      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 ${isChecked ? 'bg-brand-600' : 'bg-gray-300'}`}
                                    >
                                      <span className={`inline-block h-5 w-5 mt-0.5 rounded-full bg-white shadow transform transition-transform duration-200 ${isChecked ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Bottom save - no need to scroll back to the top */}
                    <button
                      type="button"
                      onClick={handleSavePermissions}
                      disabled={assignPermissionsMutation.isPending || permissionsLoading}
                      className="btn-primary w-full justify-center min-h-[48px] flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {assignPermissionsMutation.isPending ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span>Save Permissions</span>
                        </>
                      )}
                    </button>
                  </div>

                  <div className="hidden lg:block overflow-auto max-h-[70vh] rounded-xl border-2 border-gray-300 shadow-inner">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-gray-100 to-gray-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wide sticky left-0 top-0 bg-gradient-to-r from-gray-100 to-gray-50 z-30">
                            Module
                          </th>
                          {allActions.map((action) => (
                            <th
                              key={action}
                              className="px-6 py-4 text-center text-xs font-semibold text-gray-700 uppercase tracking-wide sticky top-0 bg-gray-100 z-20"
                            >
                              {action.replace('-', ' ')}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 bg-white">
                        {permissionMatrix.map((row, idx) => (
                          <tr key={row.module} className={`transition-all duration-300 hover:bg-gradient-to-r hover:from-brand-50 hover:to-transparent group hover:shadow-md hover:scale-[1.01] transform ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                            <td className="px-6 py-4 font-semibold text-gray-900 uppercase sticky left-0 z-10" style={{ backgroundColor: idx % 2 === 0 ? 'white' : 'rgb(249, 250, 251)' }}>
                              {row.module}
                            </td>
                            {allActions.map((action) => {
                              const permission = row.permissionsByAction[action];
                              const isChecked = permission
                                ? draftPermissionIds.includes(permission.id)
                                : false;

                              return (
                                <td key={`${row.module}-${action}`} className="px-6 py-4 text-center">
                                  {permission ? (
                                    <label
                                      title={permission.description || permission.name}
                                      className="inline-flex items-center justify-center cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => togglePermission(permission.id)}
                                        className="h-5 w-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500 cursor-pointer transition-all duration-200"
                                      />
                                    </label>
                                  ) : (
                                    <span className="text-gray-300">-</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
